import React, {useEffect, useRef, useState} from 'react';
import {DropdownButton, Dropdown, Button, Form} from "react-bootstrap";
import styled from "styled-components";
import {useTranslation} from "react-i18next";
import {childrenWithProps, isArrayEqual, useEffectArray, useOutsideAlerter} from "./utils";

const StyledDropdownButton = styled(DropdownButton)`
  display: inline-block !important;
  .dropdown-toggle {
    background-color: white;
    border-color: #ced4da;
    color: inherit;
  }
  .dropdown-menu.show {
    max-height: 300px;
    overflow: scroll;
  }
  ${props => props.variant == 'input' && `
    margin-top: 1rem;
    margin-bottom: 1rem;
    min-width: 120px;
    button {
      box-shadow: none;
      width: 100%;
      text-transform: none;
      text-align: left;
      &:after {
        position: absolute;
        top: 17px;
        right: 10px;
      }
      padding: 6px 12px;
      line-height: 24px;
      &:hover {
        box-shadow: none !important;
      }
    }
  `}
`

const StyledOption = styled.a`
  input {
    margin-top: 0.2rem;
    &:checked {
      background-color: red !important;
    }
  }
  label {
    cursor: pointer !important;
  }
  &.selected {
    /*background-color: red;*/
  }
  &.active, &:active {
    background-color: #e9ecef !important;
    color: initial !important;
  }
`

const StyledFilterGroup = styled.div`
  width: 100%;
  margin-bottom: 30px;
  > *:not(:last-child) {
    margin-right: 10px;
  }
`

export default function FilterButton({
                                       search = true,
                                       title,
                                       includeAll = false,
                                       setFilter,
                                       name: column,
                                       variant,
                                       maxElements = 2,
                                       nullIfAllSelected = true,
                                       onChange,
                                       multi = true,
                                       ...props}) {
  const { t } = useTranslation();
  const [options, setOptions] = useState([])
  const [selected, setSelected] = useState([])
  const [searchValue, setSearchValue] = useState('')
  const [filteredOptions, setFilteredOptions] = useState([])
  const [showDropdown, setShowDropdown] = useState(false)
  const [prevFilter, setPrevFilter] = useState([])

  function buildOptions(opts = []) {
    return opts.map(option => {
      if (typeof option === 'object') {
        return option
      } else {
        return {label: option, value: option}
      }
    })
  }

  useEffectArray(() => {
    setOptions(buildOptions(props.options))
  }, [props.options])

  useEffect(() => {
    setFilteredOptions(options)
    const val = props.selected ? options.filter(opt => props.selected.includes(opt.value)) : options
    setSelected(val)
    setPrevFilter(val)
  }, [options, props.selected])

  const selectedTitle = () => {
    if (selected.length == 0) {
      return t('none')
    } else if (selected.length == options.length) {
      return t('all')
    } else if (selected.length <= maxElements) {
      return selected.map(opt => opt.label).join(', ')
    } else {
      return `${selected.length} ${t('selected')}`
    }
  }

  const isSelected = (option) => selected.map(opt => opt.value).includes(option.value)

  const handleOptionClick = (option) => {
    const index = selected.map(opt => opt.value).indexOf(option.value);
    if (index > -1) {
      let selectedCopy = selected.slice()
      selectedCopy.splice(index, 1)
      setSelected(selectedCopy);
    } else {
      if (multi) {
        setSelected([...selected, option]);
      } else {
        setSelected([option]);
      }
    }
  }

  const Title = () => {
    return <><span>{title ? title + ': ' : ''}</span><span style={{fontWeight: 300}}>{selectedTitle()}</span></>
  }

  useEffect(() => {
    // debugger
    setFilteredOptions(options.filter(option => option.label.match(new RegExp(searchValue, "i"))))
  }, [searchValue])

  const wrapperRef = useOutsideAlerter(() => {
    setShowDropdown(false)
  })

  const handleToggle = (show, event, metadata) => {
    if (metadata.source === 'click' || metadata.source === 'rootClose') {
      setShowDropdown(show)
    }
  }

  const resetSearchInput = () => setSearchValue('')

  const filterValue = () => {
    if (isAllSelected() && nullIfAllSelected) {
      return null
    } else {
      return selected.map(option => option.value)
    }
  }

  const updateFilter = () => {
    if (!isArrayEqual(selected, prevFilter)) {
      const value = filterValue()
      setFilter && setFilter(column, value)
      onChange && onChange(column, value)
      setPrevFilter(selected)
    }
  }

  useEffect(() => {
    if (!showDropdown) {
      resetSearchInput()
      updateFilter()
    }
  }, [showDropdown])

  const isAllSelected = () => selected.length == options.length

  const handleSelectAll = () => {
    if (isAllSelected()) {
      setSelected([])
    } else {
      setSelected(options)
    }
  }

  return (
    <StyledDropdownButton
      title={<Title/>}
      show={showDropdown}
      onToggle={handleToggle}
      onClick={e => e.stopPropagation()}
      variant={variant}
      ref={wrapperRef}>
      {search && <div style={{padding: '5px 5px 3px 5px'}}>
        <input
          type="search"
          className="form-control form-control-sm"
          placeholder={t('table.search')}
          value={searchValue}
          style={{height: '32px'}}
          onChange={e => setSearchValue(e.target.value)}/>
      </div>}
      {includeAll && <Dropdown.Item
        key={"all"}
        as={CustomOption}
        selected={isAllSelected()}
        onClick={handleSelectAll}>
        {t('selectAll')}
      </Dropdown.Item>}
      {filteredOptions.map(option => (
        <Dropdown.Item
          key={option.value}
          as={CustomOption}
          selected={isSelected(option)}
          onClick={() => handleOptionClick(option)}>
          {option.label}
        </Dropdown.Item>
      ))}
    </StyledDropdownButton>
  )
}

function CustomOption({children, selected, onClick}) {
  return <StyledOption className={`dropdown-item ${selected ? 'selected' : ''}`} onClick={onClick}>
    <Form.Check
      type={"checkbox"}
      label={children}
      checked={selected}
      onChange={onClick}
    />
  </StyledOption>
}

export function FilterGroup(props) {
  const {children, onChange, style, fireOnChangeAtMount = false, ...filterButtonProps} = props
  const [filter, setFilter] = useState({})
  const isMounted = useRef(fireOnChangeAtMount)

  const onFilterChange = (column, value) => {
    setFilter(prevFilter => ({...prevFilter, [column]: value}))
  }

  const shouldTriggerOnChange = () => {
    return isMounted.current || fireOnChangeAtMount
  }

  useEffect(() => {
    shouldTriggerOnChange() && onChange && onChange(filter)
    isMounted.current = true
  }, [filter])

  const isGlobalFilter = (child) => child.props.hasOwnProperty("globalFilter");
  const isNotGlobalFilter = (child) => !isGlobalFilter(child)

  return(
    <StyledFilterGroup style={style}>
      {/*{childrenWithProps(React.Children.toArray(children).filter(isNotGlobalFilter), {onChange: onFilterChange, ...filterButtonProps})}*/}
      {React.Children.toArray(children).filter(isNotGlobalFilter).map(child => {
        if (React.isValidElement(child)) {
          let childProps = {
            onChange: onFilterChange,
            ...filterButtonProps
          }
          if (props.hasOwnProperty("filters")) {
            childProps.selected = props.filters?.find(f => f.id == child.props.name)?.value
          }
          return React.cloneElement(child, childProps);
        }
        return child;
      })}
      {React.Children.toArray(children).filter(isGlobalFilter)}
    </StyledFilterGroup>
  )
}
